import { useFeatureFlag } from "~/composables/useFeatureFlag";
import { useNavBarData } from "~/composables/useNavBarData";
import { usePosthog } from "~/composables/usePosthog";

export type FavoriteItem = {
  to: string;
  icon: string;
  label: string;
  sectionName: string;
  sectionColor: string;
  sectionIcon: string;
};

export const useFavorites = (opts?: { withFavoriteCleanup?: boolean }) => {
  const favorites = useLocalStorage<FavoriteItem[]>("navbar-favorites", []);
  const posthog = usePosthog();
  const { isInitialized } = useFeatureFlag();
  // Called during setup: useNavBarData uses useI18n, which throws inside a watch callback
  const navBarData = opts?.withFavoriteCleanup ? useNavBarData() : null;

  // Favorites cleanup based on available paths
  watch(isInitialized, () => {
    if (navBarData) {
      const { baseNavButtonSections } = navBarData;
      const availablePaths = baseNavButtonSections.value.flatMap((section) => section.items.map((item) => item.to));
      favorites.value = favorites.value.filter((fav) => availablePaths.includes(fav.to));
    }
  });

  const addToFavorites = (item: FavoriteItem) => {
    const exists = favorites.value.some((fav) => fav.to === item.to);
    if (!exists) {
      favorites.value.push(item);
      posthog.trackEvent("navbar_favorite_added", {
        label: item.label,
      });
    }
  };

  const removeFromFavorites = (to: string) => {
    favorites.value = favorites.value.filter((fav) => fav.to !== to);
  };

  const isFavorite = (to: string) => {
    return favorites.value.some((fav) => fav.to === to);
  };

  const toggleFavorite = (item: FavoriteItem) => {
    if (isFavorite(item.to)) {
      removeFromFavorites(item.to);
    } else {
      addToFavorites(item);
    }
  };

  return {
    favorites: readonly(favorites),
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    toggleFavorite,
  };
};
